import type { FC } from "react";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@recobra/ui/select";

import { userDisplayText } from "./user-display-name";
import { UserLabel } from "./UserLabel";

export interface UserSelectOption {
  /** Whatever the form stores — a username for Cognito lists, a `sub` elsewhere. */
  id: string;
  name?: string;
  email?: string;
}

/**
 * The one way to pick a person. Both the trigger and the list render through
 * {@link UserLabel}, so a name resolves and an avatar appears the same way in
 * every picker.
 *
 * `variant` carries the only styling difference between call sites: `field`
 * fills its form column, `pill` is the compact rounded control used inline in
 * table cells. Keep new styling here rather than at a call site — the pill
 * classes are the reason this component exists.
 */
export const UserSelect: FC<{
  value: string;
  onValueChange: (value: string) => void;
  users: readonly UserSelectOption[];
  /** Shown when nothing is selected, and as the clear option's label. */
  placeholder: string;
  /** Adds an option that clears the selection. */
  allowEmpty?: boolean;
  /** Heading above the options. */
  groupLabel?: string;
  /** Shown in place of the list when no one is eligible. */
  emptyMessage?: string;
  variant?: "field" | "pill";
  disabled?: boolean;
  /** Spinner in place of the trigger avatar while a change is saving. */
  isBusy?: boolean;
  id?: string;
  ariaLabel?: string;
}> = ({
  value,
  onValueChange,
  users,
  placeholder,
  allowEmpty = false,
  groupLabel,
  emptyMessage,
  variant = "field",
  disabled = false,
  isBusy = false,
  id,
  ariaLabel,
}) => {
  const findUser = (candidate: string) => users.find((user) => user.id === candidate);

  const options = (
    <>
      {allowEmpty ? <SelectItem value="">{placeholder}</SelectItem> : null}
      {users.map((user) => (
        <SelectItem key={user.id} value={user.id}>
          <UserLabel userId={user.id} name={user.name} />
        </SelectItem>
      ))}
      {users.length === 0 && emptyMessage ? (
        <p className="px-2.5 py-1.5 text-center text-sm text-muted-foreground">{emptyMessage}</p>
      ) : null}
    </>
  );

  return (
    <Select
      value={value}
      onValueChange={(next) => onValueChange(next ?? "")}
      disabled={disabled}
      itemToStringLabel={(candidate) =>
        candidate ? userDisplayText(findUser(candidate)) : placeholder
      }
    >
      <SelectTrigger
        id={id}
        aria-label={ariaLabel}
        className={variant === "pill" ? "max-w-full rounded-full pl-1" : "w-full"}
      >
        <SelectValue placeholder={placeholder}>
          {(selected: string) =>
            selected ? (
              <UserLabel
                userId={selected}
                name={findUser(selected)?.name}
                emptyLabel={placeholder}
                isBusy={isBusy}
              />
            ) : (
              placeholder
            )
          }
        </SelectValue>
      </SelectTrigger>
      <SelectContent alignItemWithTrigger={false}>
        {groupLabel ? (
          <SelectGroup>
            <SelectLabel>{groupLabel}</SelectLabel>
            {options}
          </SelectGroup>
        ) : (
          options
        )}
      </SelectContent>
    </Select>
  );
};
